import axios from 'axios'
import type { AxiosInstance, AxiosTransformer } from 'axios'
import { createApiClient } from '@clouway/api'
import type { ApiClient } from '@clouway/api'

import { reviveDates, serializeDates, serializeParams } from './dates'
import { NetworkError } from './errors'

export interface CreateClientOptions {
  baseURL?: string
  /** Invoked on a 401 response; defaults to a full-page /auth/login navigation. */
  onUnauthenticated?: () => void
  configure?: (instance: AxiosInstance) => void
}

function redirectToLogin() {
  window.location.assign('/auth/login')
}

export function createClient(options: CreateClientOptions = {}): ApiClient {
  // /bff is the cookie-authenticated browser tree on the gateway — kept
  // separate from /api/v1 (fproxy/bearer ingress for the mobile app).
  const { baseURL = '/bff', onUnauthenticated = redirectToLogin, configure } = options

  return createApiClient({
    baseURL,
    configure: (instance) => {
      instance.defaults.paramsSerializer = serializeParams
      instance.defaults.transformRequest = [
        (data: unknown) => serializeDates(data),
        ...(axios.defaults.transformRequest as AxiosTransformer[]),
      ]
      instance.interceptors.response.use(
        (response) => {
          response.data = reviveDates(response.data)
          return response
        },
        (error: unknown) => {
          if (axios.isAxiosError(error)) {
            if (error.response === undefined) {
              return Promise.reject(new NetworkError(error.message || 'Network error', { cause: error }))
            }
            if (error.response.status === 401) {
              onUnauthenticated()
            }
          }
          return Promise.reject(error)
        },
      )
      configure?.(instance)
    },
  })
}

export const api = createClient()
